import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import SelectField from 'Shared/form/SelectField';
import { TextField } from 'Shared/form/TextField';
import { CheckboxField } from 'Shared/form/CheckboxField';
import { useFormikContext } from 'formik';
import * as R from 'ramda';
import {
    booleanOptions,
    denialReasonOptions,
    faSourceOptions,
    disableActivityStatusList,
    STATUS_TYPE,
} from '../constants';
import { DatePickerField } from 'Shared/form/DatePickerField';
import { FaExclamationCircle } from 'react-icons/fa';
import FAMedicationTable from './FAMedicationTable';
import { NumberField } from 'Shared/form/NumberField';
import PropTypes from 'prop-types';
import { PhoneField } from 'App/components/PatientForm/components/PhoneField';
import SelectedProgramTable from './SelectedProgramTable';
import AvailableProgramsTable from './AvailableProgramsTable';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import AmountFundedToolTip from './AmountFundedToolTip';
import PopoverTooltip from 'App/components/Popovers/PopoverTooltip';
import CommonPatientFieldWrapper from 'App/components/PatientForm/components/CommonPatientFieldWrapper';
import { TextareaField } from 'Shared/form/TextareaField';

const propTypes = {
    isFormReadOnly: PropTypes.bool.isRequired,
};

const defaultProps = {};

const tooltip = <span id="actions-tooltip"> Must have an applicable protocol enabled. </span>;

function setInitialStatus(statusNames) {
    return R.compose(
        R.map(
            R.applySpec({
                id: R.identity,
                name: R.identity,
                disabled: R.compose(R.ifElse(R.equals(STATUS_TYPE.new), R.T, R.F)),
            })
        ),
        R.values,
        R.pick(statusNames)
    )(STATUS_TYPE);
}

function FAInformationForm({ isFormReadOnly }) {
    const { initialValues, values, setTouched, touched, setValues } = useFormikContext();

    const originalStatus = R.path(['status', 'id'], initialValues);

    const statusOptions = useMemo(() => {
        if (originalStatus === STATUS_TYPE.new) {
            return setInitialStatus(['new', 'pendingSubmission', 'submitted']);
        }

        if (originalStatus === STATUS_TYPE.pendingSubmission) {
            return setInitialStatus(['pendingSubmission', 'submitted']);
        }

        if (originalStatus === STATUS_TYPE.submitted) {
            return setInitialStatus(['pendingSubmission', 'submitted', 'partiallyApproved', 'approved', 'denied']);
        }

        return R.compose(
            R.map(R.applySpec({ id: R.identity, name: R.identity, disabled: R.F })),
            R.values
        )(STATUS_TYPE);
    }, [originalStatus]);

    return (
        <>
            <Row>
                <Col md={3}>
                    <SelectField
                        options={statusOptions}
                        label="Status"
                        fieldName="status"
                        getOptionValue={R.prop('id')}
                        getOptionLabel={R.prop('name')}
                        isDisabled={isFormReadOnly}
                        onChange={(option) => {
                            let nextValues = values;
                            let nextTouched = touched;

                            if (
                                STATUS_TYPE.pendingSubmission === option?.id ||
                                STATUS_TYPE.submitted === option?.id ||
                                STATUS_TYPE.new === option?.id ||
                                STATUS_TYPE.denied === option?.id
                            ) {
                                nextValues = R.compose(
                                    R.set(R.lensPath(['details', 'effectiveStartDate']), null),
                                    R.set(R.lensPath(['details', 'effectiveEndDate']), null),
                                    R.set(R.lensPath(['details', 'amountFunded']), null),
                                    R.set(R.lensPath(['details', 'otherAssistance']), null),
                                    R.set(R.lensPath(['details', 'fundingSource']), null),
                                    R.set(R.lensPath(['details', 'isCoveringMedicationCost']), null),
                                    R.set(R.lensPath(['details', 'isReEnrollmentRequired']), null),
                                    R.set(R.lensPath(['details', 'fundingSourceOther']), null)
                                )(nextValues);

                                nextTouched = R.compose(
                                    R.set(R.lensPath(['details', 'applicationSubmission']), null),
                                    R.set(R.lensPath(['details', 'effectiveStartDate']), null),
                                    R.set(R.lensPath(['details', 'effectiveEndDate']), null),
                                    R.set(R.lensPath(['details', 'amountFunded']), null),
                                    R.set(R.lensPath(['details', 'otherAssistance']), null),
                                    R.set(R.lensPath(['details', 'fundingSource']), null),
                                    R.set(R.lensPath(['details', 'isCoveringMedicationCost']), null),
                                    R.set(R.lensPath(['details', 'isReEnrollmentRequired']), null),
                                    R.set(R.lensPath(['details', 'fundingSourceOther']), null)
                                )(touched);
                            }

                            if (STATUS_TYPE.denied !== option?.id) {
                                nextValues = R.compose(R.set(R.lensPath(['details', 'denialReason']), null))(
                                    nextValues
                                );

                                nextTouched = R.compose(R.set(R.lensPath(['details', 'denialReason']), false))(
                                    nextTouched
                                );
                            }

                            if (STATUS_TYPE.denied === option?.id) {
                                nextValues = R.compose(
                                    R.set(R.lensPath(['details', 'fileApplication']), { id: 'No', name: 'No' })
                                )(nextValues);

                                nextTouched = R.compose(R.set(R.lensPath(['details', 'fileApplication']), false))(
                                    nextTouched
                                );
                            }

                            if (
                                STATUS_TYPE.pendingSubmission === option?.id ||
                                STATUS_TYPE.new === option?.id ||
                                STATUS_TYPE.denied === option?.id
                            ) {
                                nextValues = R.compose(R.set(R.lensPath(['details', 'applicationSubmission']), null))(
                                    nextValues
                                );

                                nextTouched = R.compose(R.set(R.lensPath(['details', 'applicationSubmission']), false))(
                                    nextTouched
                                );
                            }

                            R.compose(setValues, R.set(R.lensProp('status'), option))(nextValues);

                            setTouched(nextTouched);
                        }}
                    />
                </Col>

                {values?.status?.id !== STATUS_TYPE.denied && (
                    <CommonPatientFieldWrapper fieldName="referralFaDidPatientFileApplication">
                        {(commonProps) => (
                            <Col md={3}>
                                <SelectField
                                    {...commonProps}
                                    options={booleanOptions.map((it) => ({ id: it, name: it }))}
                                    label="Did the patient file an application?"
                                    fieldName="details.fileApplication"
                                    getOptionValue={R.prop('id')}
                                    getOptionLabel={R.prop('name')}
                                    isDisabled={commonProps.disabled || isFormReadOnly}
                                />
                            </Col>
                        )}
                    </CommonPatientFieldWrapper>
                )}
                {[
                    STATUS_TYPE.submitted,
                    STATUS_TYPE.deleted,
                    STATUS_TYPE.partiallyApproved,
                    STATUS_TYPE.approved,
                ].includes(values?.status?.id) && (
                    <CommonPatientFieldWrapper fieldName="referralFaApplicationSubmissionDate">
                        {(commonProps) => (
                            <Col md={3}>
                                <DatePickerField
                                    {...commonProps}
                                    fieldName="details.applicationSubmission"
                                    label="Application Submission Date"
                                    disabled={commonProps.disabled || isFormReadOnly}
                                />
                            </Col>
                        )}
                    </CommonPatientFieldWrapper>
                )}
            </Row>

            <FAMedicationTable />

            {[STATUS_TYPE.new, STATUS_TYPE.pendingSubmission, STATUS_TYPE.submitted].includes(
                initialValues?.status?.id
            ) && (
                <AccordionWrapper title="Available Programs" stateKey="faAvailablePrograms-toggle">
                    <Row>
                        <Col md={9}>
                            <AvailableProgramsTable />
                        </Col>
                    </Row>
                </AccordionWrapper>
            )}

            {(values?.details?.programId && disableActivityStatusList.includes(values?.status?.id)) ||
            [STATUS_TYPE.new, STATUS_TYPE.pendingSubmission, STATUS_TYPE.submitted].includes(
                initialValues?.status?.id
            ) ? (
                <AccordionWrapper title="Selected Program" stateKey="faSelectedProgram-toggle">
                    <Row>
                        <Col md={9}>
                            {(values?.status?.id === STATUS_TYPE.partiallyApproved ||
                                values?.status?.id === STATUS_TYPE.approved) && (
                                <p
                                    style={{
                                        color: 'var(--brandDanger)',
                                    }}
                                >
                                    Either a selected program or “Financial Assistance Source” is required.
                                </p>
                            )}
                            <SelectedProgramTable />
                        </Col>
                    </Row>
                </AccordionWrapper>
            ) : null}

            <div style={{ marginTop: '20px' }}>
                <Row>
                    <CommonPatientFieldWrapper fieldName="referralFaFollowUpPhoneNumber">
                        {(commonProps) => (
                            <Col md={3}>
                                <PhoneField
                                    {...commonProps}
                                    disabled={commonProps.disabled || isFormReadOnly}
                                    fieldName="details.followUpPhone"
                                    label="Follow Up Phone Number"
                                    placeholder="(###) ###-####"
                                />
                            </Col>
                        )}
                    </CommonPatientFieldWrapper>
                </Row>
            </div>

            {(values?.status?.id === STATUS_TYPE.partiallyApproved || values?.status?.id === STATUS_TYPE.approved) && (
                <>
                    <Row>
                        <CommonPatientFieldWrapper fieldName="referralFaEffectiveStartDate">
                            {(commonProps) => (
                                <Col md={3}>
                                    <DatePickerField
                                        {...commonProps}
                                        fieldName="details.effectiveStartDate"
                                        label="Effective Start Date"
                                        disabled={commonProps.disabled || isFormReadOnly}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="referralFaEffectiveEndDate">
                            {(commonProps) => (
                                <Col md={3}>
                                    <DatePickerField
                                        {...commonProps}
                                        fieldName="details.effectiveEndDate"
                                        label="Effective End Date"
                                        disabled={commonProps.disabled || isFormReadOnly}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="referralFaAmountFunded">
                            {(commonProps) => (
                                <Col md={3} data-testid="xxw">
                                    <NumberField
                                        {...commonProps}
                                        label={
                                            <>
                                                Amount Funded{' '}
                                                <PopoverTooltip
                                                    tooltipContent={AmountFundedToolTip()}
                                                    tooltipId="amountfundedIcon"
                                                    icon={<FaExclamationCircle />}
                                                />
                                            </>
                                        }
                                        placeholder="Amount Funded"
                                        disabled={commonProps.disabled || isFormReadOnly}
                                        fieldName="details.amountFunded"
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                    </Row>

                    <Row>
                        <CommonPatientFieldWrapper fieldName="referralFaOtherAssistanceApproved">
                            {(commonProps) => (
                                <Col md={3}>
                                    <NumberField
                                        {...commonProps}
                                        label="Other Assistance Approved"
                                        placeholder="Other Assistance Approved"
                                        disabled={commonProps.disabled || isFormReadOnly}
                                        fieldName="details.otherAssistance"
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="referralFaFinancialAssistanceSource">
                            {(commonProps) => (
                                <Col md={3}>
                                    <SelectField
                                        {...commonProps}
                                        options={faSourceOptions.map((it) => ({ id: it, name: it }))}
                                        label="Financial Assistance Source?"
                                        fieldName="details.fundingSource"
                                        required={commonProps.required || !values?.details?.programId}
                                        requiredErrorText="Either a selected program or “Financial Assistance Source” is required."
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                        isDisabled={commonProps.disabled || isFormReadOnly}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="referralFaIsItCoveringPartAllOfMedicationCost">
                            {(commonProps) => (
                                <Col md={3}>
                                    <SelectField
                                        {...commonProps}
                                        options={booleanOptions.map((it) => ({ id: it, name: it }))}
                                        label="Is it covering part/all of medication cost?"
                                        fieldName="details.isCoveringMedicationCost"
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                        isDisabled={commonProps.disabled || isFormReadOnly}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                    </Row>
                    <Row>
                        <CommonPatientFieldWrapper fieldName="referralFaIsReEnrollmentToTheProgramRequired">
                            {(commonProps) => (
                                <Col md={3}>
                                    <SelectField
                                        {...commonProps}
                                        options={booleanOptions.map((it) => ({ id: it, name: it }))}
                                        label="Is re-enrollment to the program required?"
                                        fieldName="details.isReEnrollmentRequired"
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                        isDisabled={commonProps.disabled || isFormReadOnly}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        {values?.details?.fundingSource?.id === 'Other' && (
                            <CommonPatientFieldWrapper fieldName="referralFaOtherSource">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <TextField
                                            {...commonProps}
                                            label="Other Source?"
                                            required
                                            fieldName="details.fundingSourceOther"
                                            disabled={commonProps.disabled || isFormReadOnly}
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>
                        )}
                    </Row>

                    <Row>
                        <CommonPatientFieldWrapper fieldName="referralFaCreateRenewalActivity">
                            {(commonProps) => (
                                <Col md={6}>
                                    <CheckboxField
                                        {...commonProps}
                                        disabled={commonProps.disabled || isFormReadOnly}
                                        fieldName="details.scheduleFollowUpActivity"
                                        label={
                                            <>
                                                Create a Financial Assistance Renewal activity{' '}
                                                <PopoverTooltip
                                                    tooltipContent={tooltip}
                                                    tooltipId="icon"
                                                    icon={<FaExclamationCircle />}
                                                />
                                            </>
                                        }
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                    </Row>
                </>
            )}

            {values?.status?.id === STATUS_TYPE.denied && (
                <Row>
                    <CommonPatientFieldWrapper fieldName="referralFaDenialReason">
                        {(commonProps) => (
                            <Col md={3}>
                                <SelectField
                                    {...commonProps}
                                    options={denialReasonOptions.map((it) => ({ id: it, name: it }))}
                                    label="Denial Reason"
                                    fieldName="details.denialReason"
                                    getOptionValue={R.prop('id')}
                                    getOptionLabel={R.prop('name')}
                                    isDisabled={commonProps.disabled || isFormReadOnly}
                                />
                            </Col>
                        )}
                    </CommonPatientFieldWrapper>
                </Row>
            )}

            <Row>
                <CommonPatientFieldWrapper fieldName="referralFaNotes">
                    {(commonProps) => (
                        <Col md={9}>
                            <TextareaField
                                {...commonProps}
                                fieldName="details.notes"
                                label="Notes"
                                placeholder="Notes"
                                disabled={commonProps.disabled || isFormReadOnly}
                                rows={3}
                            />
                        </Col>
                    )}
                </CommonPatientFieldWrapper>
            </Row>
        </>
    );
}

FAInformationForm.propTypes = propTypes;
FAInformationForm.defaultProps = defaultProps;

export default FAInformationForm;
